import { LitElement, html, nothing, type TemplateResult, type PropertyValues } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import styles from './styles.js';
import type { MenuItem, MenuItemSelectEventDetail, Placement } from '../types.js';
import type { PanelMenuItem } from '../panelmenu/index.js';
import type { UwcMenu } from '../menu/index.js';
import { emit } from '../utils/dom.utils.js';

/* ─────────────────────────────────────────────────────────────────────────────
   Re-export the item model — uwc-sidebar consumes the same shape as
   uwc-panelmenu so the two are drop-in interchangeable.
───────────────────────────────────────────────────────────────────────────── */
export type { PanelMenuItem } from '../panelmenu/index.js';

/* ─────────────────────────────────────────────────────────────────────────────
   Event detail types
───────────────────────────────────────────────────────────────────────────── */

export interface SidebarToggleEventDetail {
  /** Desktop: rail collapsed. Mobile: drawer closed. */
  collapsed: boolean;
  /** True when the toggle happened in mobile (drawer) mode. */
  mobile: boolean;
}

/** Minimal structural type for the native Invoker `CommandEvent`. */
interface CommandLikeEvent extends Event { readonly command?: string; readonly source?: Element | null; }

/* ─────────────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────────────── */

/**
 * `uwc-sidebar`
 *
 * A collapsing dashboard navigation rail driven by the same `PanelMenuItem[]`
 * model as `uwc-panelmenu`.
 *
 * - **Expanded** — renders an inline accordion that is visually and behaviourally
 *   identical to `uwc-panelmenu` (it composes `uwc-panelmenu` internally).
 * - **Collapsed** — narrows to an icon-only rail. Leaf items reveal their label
 *   in a `uwc-tooltip` on hover/focus; parent items open their children in a
 *   floating `uwc-menu` flyout (hover, click, or keyboard).
 *
 * Collapse / expand is controlled **externally** — there is no built-in toggle
 * button. Drive it any of three ways:
 *
 * 1. Native Invoker Commands — `<uwc-button commandfor="sb" command="--toggle">`
 *    (also `--collapse` / `--expand`). No JavaScript required.
 * 2. Imperative API — `el.toggle()`, `el.collapse()`, `el.expand()`.
 * 3. Reflected attribute / property — `el.collapsed = true` or `collapsed` attr.
 *
 * Three slots compose a standard dashboard shell:
 *
 * - `start` — brand / logo region pinned to the top.
 * - *(middle)* — the navigation, rendered from the `items` model.
 * - `end` — settings / profile / logout region pinned to the bottom.
 *
 * ## Responsive (mobile) behaviour
 *
 * At/below `mobile-breakpoint` (default 768px) the sidebar switches to mobile
 * mode: it is **hidden by default** and `toggle()` (or `command="--toggle"`)
 * opens it as a `uwc-drawer` overlay sliding in from the left, with backdrop,
 * focus trap, Escape and outside-click dismiss. The full accordion is shown in
 * the drawer (never the icon rail). The `start` / `end` slots flow into the
 * drawer's header / footer. Above the breakpoint it is the inline rail again.
 *
 * @attr items                  — `PanelMenuItem[]` navigation model.
 * @attr collapsed              — Reflected. Renders the icon-only rail (desktop).
 * @attr multiple               — Allow multiple accordion panels open at once (expanded mode). Default: `true`.
 * @attr flyout-placement       — Placement for collapsed-mode flyouts. Default: `right-start`.
 * @attr hover-delay            — ms before a collapsed flyout opens on hover. Default: `120`.
 * @attr mobile-breakpoint      — Max viewport px for mobile/drawer mode. Default: `768`.
 * @attr drawer-size            — Width of the mobile drawer overlay. Default: `17rem`.
 * @attr aria-label             — Accessible name for the navigation landmark. Default: `'Main navigation'`.
 *
 * @prop activeItem             — The selected item (matched by `id`, else by reference).
 *                                Two-way: updates on selection. Set `null` to clear.
 *
 * @fires uwc-sidebar-toggle    — `{ collapsed, mobile }` — collapse/expand (desktop) or drawer close/open (mobile).
 * @fires uwc-item-select       — `{ item, originalEvent }` — leaf item activated (either mode).
 * @fires uwc-panel-open        — `{ item }` — accordion panel expanded (expanded mode).
 * @fires uwc-panel-close       — `{ item }` — accordion panel collapsed (expanded mode).
 * @fires uwc-item-expand       — `{ item, key }` — nested item expanded (expanded mode).
 * @fires uwc-item-collapse     — `{ item, key }` — nested item collapsed (expanded mode).
 *
 * @slot start                  — Brand / header content (top).
 * @slot end                    — Footer content: settings, profile, logout (bottom).
 *
 * @csspart base                — Root `<aside>` element.
 * @csspart start               — Header region wrapper.
 * @csspart nav                 — Navigation region wrapper.
 * @csspart end                 — Footer region wrapper.
 * @csspart rail                — The icon rail `<ul>` (collapsed mode).
 * @csspart rail-item           — A rail trigger button (collapsed mode).
 *
 * @cssprop --uwc-sidebar-width            Expanded width. Default: 260px.
 * @cssprop --uwc-sidebar-collapsed-width  Collapsed rail width. Default: 72px.
 * @cssprop --uwc-sidebar-bg
 * @cssprop --uwc-sidebar-color
 * @cssprop --uwc-sidebar-border
 * @cssprop --uwc-sidebar-duration         Collapse animation duration. Default: 220ms.
 * @cssprop --uwc-sidebar-rail-item-size   Rail button hit area. Default: 44px.
 * @cssprop --uwc-sidebar-item-hover-bg
 * @cssprop --uwc-sidebar-item-active-bg
 * @cssprop --uwc-sidebar-item-active-color
 */
export class UwcSidebar extends LitElement {
  static override styles = styles;

  /* ── Public properties ───────────────────────────────────────────── */

  @property({ type: Array })
  items: PanelMenuItem[] = [];

  @property({ type: Boolean, reflect: true })
  collapsed = false;

  @property({ type: Boolean, reflect: true })
  multiple = true;

  @property({ attribute: 'flyout-placement' })
  flyoutPlacement: Placement = 'right-start';

  @property({ type: Number, attribute: 'hover-delay' })
  hoverDelay = 120;

  /**
   * Max viewport width (px) at which the sidebar switches to mobile mode:
   * hidden by default, opening as a `uwc-drawer` overlay on toggle. Default: 768.
   */
  @property({ type: Number, attribute: 'mobile-breakpoint' })
  mobileBreakpoint = 768;

  /** Width of the mobile drawer overlay. Default: `17rem`. */
  @property({ attribute: 'drawer-size' })
  drawerSize = '17rem';

  @property({ attribute: 'aria-label', reflect: true })
  override ariaLabel = 'Main navigation';

  /**
   * The currently selected item. Matched by `id` first, then by object
   * reference. Set to `null` to clear. Updated automatically on selection.
   */
  @property({ attribute: false })
  activeItem: PanelMenuItem | null = null;

  /* ── Internal refs / state ───────────────────────────────────────── */

  @query('.sb__nav') private _nav!: HTMLElement;

  /** The flyout menu currently open in collapsed mode (if any). */
  private _openFlyout: UwcMenu | null = null;

  /** Roving-focus index across rail buttons (collapsed mode). */
  @state() private _railFocusIdx = 0;

  /** True when the viewport is at/below `mobileBreakpoint`. */
  @state() private _isMobile = false;

  /** Mobile drawer open state (only meaningful when `_isMobile`). */
  @state() private _drawerOpen = false;

  private _flyoutShowTimer: ReturnType<typeof setTimeout> | null = null;
  private _flyoutHideTimer: ReturnType<typeof setTimeout> | null = null;

  private _mql: MediaQueryList | null = null;

  private readonly _onCommand   = this._handleCommand.bind(this);
  private readonly _onMqChange  = (e: MediaQueryListEvent): void => this._applyMobile(e.matches);

  /**
   * Capturing rail keydown listener (object form so Lit attaches it with
   * `capture: true` to whichever nav element is currently rendered). Capturing
   * lets it run before uwc-menu's own trigger keydown so roving navigation and
   * submenu opening never clash — and it re-binds on every render, avoiding the
   * stale-reference trap of a manual `firstUpdated` attach.
   */
  private readonly _railKeydown: EventListenerObject & AddEventListenerOptions = {
    handleEvent: (e: Event) => this._handleRailKeydown(e as KeyboardEvent),
    capture: true,
  };

  /* ── Lifecycle ───────────────────────────────────────────────────── */

  override connectedCallback(): void {
    super.connectedCallback();
    // Native Invoker Commands (command/commandfor) dispatch a `command` event
    // on this element. No JS wiring needed on the consumer side.
    this.addEventListener('command', this._onCommand as EventListener);
    this._setupMedia();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('command', this._onCommand as EventListener);
    this._mql?.removeEventListener('change', this._onMqChange);
    this._mql = null;
    this._clearFlyoutTimers();
  }

  protected override willUpdate(changed: PropertyValues): void {
    if (changed.has('mobileBreakpoint')) this._setupMedia();
    if (changed.has('collapsed')) {
      // Leaving collapsed mode — tear down any open flyout.
      this._closeOpenFlyout();
      this._railFocusIdx = Math.min(this._railFocusIdx, Math.max(0, this._visibleItems.length - 1));
    }
  }

  protected override updated(changed: PropertyValues): void {
    if (changed.has('_isMobile')) this.toggleAttribute('mobile', this._isMobile);
  }

  /* ── Responsive (mobile drawer) ──────────────────────────────────── */

  private _setupMedia(): void {
    this._mql?.removeEventListener('change', this._onMqChange);
    this._mql = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`);
    this._mql.addEventListener('change', this._onMqChange);
    this._applyMobile(this._mql.matches);
  }

  private _applyMobile(isMobile: boolean): void {
    if (this._isMobile === isMobile) return;
    this._isMobile = isMobile;
    // Always start hidden when entering mobile, and drop the flyout/drawer when
    // leaving it, so neither mode inherits the other's open state.
    this._drawerOpen = false;
    this._closeOpenFlyout();
  }

  /* ── Derived data ────────────────────────────────────────────────── */

  private get _visibleItems(): PanelMenuItem[] {
    return this.items.filter(it => it.visible !== false);
  }

  /** True when `activeItem` is `item` or lives anywhere inside its subtree. */
  private _containsActive(item: PanelMenuItem): boolean {
    if (!this.activeItem) return false;
    const matches = (a: PanelMenuItem, b: PanelMenuItem): boolean =>
      a.id && b.id ? a.id === b.id : a === b;

    const walk = (node: PanelMenuItem): boolean => {
      if (matches(node, this.activeItem!)) return true;
      return (node.items ?? []).some(walk);
    };
    return walk(item);
  }

  /* ── Public API ──────────────────────────────────────────────────── */

  /**
   * Toggle the sidebar. Viewport-aware:
   * - Mobile  → open / close the drawer overlay.
   * - Desktop → collapse / expand the icon rail.
   */
  toggle(): void {
    this._isMobile ? this._setDrawer(!this._drawerOpen) : this._setCollapsed(!this.collapsed);
  }

  /** Mobile → close the drawer. Desktop → collapse to the icon rail. */
  collapse(): void {
    this._isMobile ? this._setDrawer(false) : this._setCollapsed(true);
  }

  /** Mobile → open the drawer. Desktop → expand to the full accordion. */
  expand(): void {
    this._isMobile ? this._setDrawer(true) : this._setCollapsed(false);
  }

  /** Whether the sidebar is currently rendering as a mobile drawer. */
  get isMobile(): boolean { return this._isMobile; }

  /** Whether the mobile drawer is open (always false on desktop). */
  get drawerOpen(): boolean { return this._isMobile && this._drawerOpen; }

  private _setCollapsed(next: boolean): void {
    if (this.collapsed === next) return;
    this.collapsed = next;
    emit<SidebarToggleEventDetail>(this, 'uwc-sidebar-toggle', { collapsed: next, mobile: false });
  }

  private _setDrawer(open: boolean): void {
    if (this._drawerOpen === open) return;
    this._drawerOpen = open;
    emit<SidebarToggleEventDetail>(this, 'uwc-sidebar-toggle', { collapsed: !open, mobile: true });
  }

  /** True when the consumer has provided light-DOM content for the named slot. */
  private _hasSlotted(name: string): boolean {
    return Array.from(this.children).some(c => (c as HTMLElement).slot === name);
  }

  /* ── Native Invoker command handling ─────────────────────────────── */

  private _handleCommand(e: CommandLikeEvent): void {
    switch (e.command) {
      case '--toggle':   this.toggle();   break;
      case '--collapse': this.collapse(); break;
      case '--expand':   this.expand();   break;
    }
  }

  /* ── Selection ───────────────────────────────────────────────────── */

  private _selectItem(item: PanelMenuItem, originalEvent: Event): void {
    if (item.disabled) return;
    this.activeItem = item;
    item.command?.({ originalEvent, item: item as unknown as MenuItem });
    emit<MenuItemSelectEventDetail>(this, 'uwc-item-select', {
      item: item as unknown as MenuItem,
      originalEvent,
    });
  }

  /* ── Expanded mode (composes uwc-panelmenu) ──────────────────────── */

  private _relaySelect = (e: CustomEvent<MenuItemSelectEventDetail>): void => {
    this.activeItem = e.detail.item as unknown as PanelMenuItem;
    // uwc-panelmenu already emits uwc-item-select; let it bubble through.
  };

  private _renderExpanded(): TemplateResult {
    return html`
      <uwc-panelmenu
        class="sb__panelmenu"
        exportparts="header, item, badge"
        .items=${this.items}
        .activeItem=${this.activeItem}
        ?multiple=${this.multiple}
        aria-label=${this.ariaLabel}
        @uwc-item-select=${this._relaySelect}
      ></uwc-panelmenu>`;
  }

  /* ── Collapsed mode (icon rail) ──────────────────────────────────── */

  private _railIcon(item: PanelMenuItem): TemplateResult {
    if (item.icon) {
      return html`<uwc-icon
        name=${item.icon}
        size="1.3em"
        library=${item.iconLibrary ?? 'default'}
        class=${item.iconClass ?? ''}
      ></uwc-icon>`;
    }
    const initial = (item.label ?? '?').trim().charAt(0).toUpperCase() || '?';
    return html`<span class="rail__initial" aria-hidden="true">${initial}</span>`;
  }

  private _renderRailButton(item: PanelMenuItem, idx: number, hasChildren: boolean): TemplateResult {
    const isActive = this._containsActive(item);
    return html`
      <button
        slot="trigger"
        part="rail-item"
        type="button"
        class=${classMap({
          'rail__btn':           true,
          'rail__btn--active':   isActive,
          'rail__btn--disabled': !!item.disabled,
          'rail__btn--danger':   !!item.danger,
        })}
        data-rail-idx=${idx}
        ?disabled=${item.disabled}
        tabindex=${idx === this._railFocusIdx ? '0' : '-1'}
        aria-label=${item.label ?? ''}
        aria-current=${isActive && !hasChildren ? 'true' : 'false'}
        title=${item.title ?? ''}
        @focus=${() => { this._railFocusIdx = idx; }}
        @click=${(e: MouseEvent) => { if (!hasChildren) this._selectItem(item, e); }}
      >
        ${this._railIcon(item)}
        ${item.badge ? html`<span class="rail__dot" aria-hidden="true"></span>` : nothing}
      </button>`;
  }

  private _renderRailItem(item: PanelMenuItem, idx: number): TemplateResult {
    if (item.visible === false) return html``;
    const hasChildren = Boolean(item.items?.length);

    if (hasChildren) {
      // Parent → floating uwc-menu flyout. Hover handlers live on the
      // <uwc-menu> host so moving the pointer onto the panel keeps it open.
      return html`
        <li class="rail__item" role="none">
          <uwc-menu
            class="rail__flyout"
            placement=${this.flyoutPlacement}
            .offset=${12}
            .items=${(item.items ?? []) as unknown as MenuItem[]}
            @mouseenter=${this._onFlyoutHostEnter}
            @mouseleave=${this._onFlyoutHostLeave}
            @uwc-show=${(e: Event) => { this._openFlyout = e.currentTarget as UwcMenu; }}
            @uwc-hide=${(e: Event) => { if (this._openFlyout === e.currentTarget) this._openFlyout = null; }}
            @uwc-item-select=${(e: CustomEvent<MenuItemSelectEventDetail>) => this._onFlyoutSelect(item, e)}
          >
            <div slot="start" class="rail__flyout-head">${item.label ?? ''}</div>
            ${this._renderRailButton(item, idx, true)}
          </uwc-menu>
        </li>`;
    }

    // Leaf → label tooltip on hover/focus.
    return html`
      <li class="rail__item" role="none">
        <uwc-tooltip content=${item.label ?? ''} placement="right" .offset=${14}>
          ${this._renderRailButton(item, idx, false)}
        </uwc-tooltip>
      </li>`;
  }

  private _onFlyoutSelect(parent: PanelMenuItem, e: CustomEvent<MenuItemSelectEventDetail>): void {
    // The child is the real selection; surface the parent rail as active too.
    this.activeItem = e.detail.item as unknown as PanelMenuItem;
    void parent;
    // uwc-menu already re-emits uwc-item-select (composed event) — let it bubble.
  }

  private _renderCollapsed(): TemplateResult {
    return html`
      <ul class="rail" part="rail" role="menubar" aria-orientation="vertical" aria-label=${this.ariaLabel}>
        ${repeat(
          this._visibleItems,
          (item, i) => item.id ?? `s-${i}`,
          (item, i) => this._renderRailItem(item, i),
        )}
      </ul>`;
  }

  /* ── Collapsed flyout hover intent ───────────────────────────────── */

  private _onFlyoutHostEnter = (e: Event): void => {
    const menu = e.currentTarget as UwcMenu;
    this._clearFlyoutTimers();
    this._flyoutShowTimer = setTimeout(() => {
      if (this._openFlyout && this._openFlyout !== menu) this._openFlyout.hide();
      menu.show();
    }, this.hoverDelay);
  };

  private _onFlyoutHostLeave = (e: Event): void => {
    const menu = e.currentTarget as UwcMenu;
    if (this._flyoutShowTimer !== null) { clearTimeout(this._flyoutShowTimer); this._flyoutShowTimer = null; }
    this._flyoutHideTimer = setTimeout(() => menu.hide(), 180);
  };

  private _clearFlyoutTimers(): void {
    if (this._flyoutShowTimer !== null) { clearTimeout(this._flyoutShowTimer); this._flyoutShowTimer = null; }
    if (this._flyoutHideTimer !== null) { clearTimeout(this._flyoutHideTimer); this._flyoutHideTimer = null; }
  }

  private _closeOpenFlyout(): void {
    this._clearFlyoutTimers();
    this._openFlyout?.hide();
    this._openFlyout = null;
  }

  /* ── Rail keyboard (capturing) ───────────────────────────────────── */

  private _railButtons(): HTMLButtonElement[] {
    return Array.from(this._nav.querySelectorAll<HTMLButtonElement>('.rail__btn:not([disabled])'));
  }

  private _focusRail(idx: number): void {
    const btns = this._railButtons();
    if (!btns.length) return;
    const clamped = (idx + btns.length) % btns.length;
    const btn = btns[clamped];
    this._railFocusIdx = Number(btn.dataset.railIdx ?? clamped);
    btn.focus();
  }

  private _handleRailKeydown(e: KeyboardEvent): void {
    if (!this.collapsed) return;
    const btns    = this._railButtons();
    // The capturing listener lives in the same shadow root as the buttons, so
    // `e.target` is the focused button itself (no retargeting within a root).
    const curBtn  = (e.target as HTMLElement)?.closest?.('.rail__btn') as HTMLButtonElement | null;
    const cur     = curBtn ? btns.indexOf(curBtn) : -1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation(); // suppress uwc-menu's own ArrowDown-to-open
        this._focusRail(cur + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        this._focusRail(cur - 1);
        break;
      case 'Home':
        e.preventDefault();
        e.stopPropagation();
        this._focusRail(0);
        break;
      case 'End':
        e.preventDefault();
        e.stopPropagation();
        this._focusRail(btns.length - 1);
        break;
      case 'ArrowRight': {
        // Open the focused parent's flyout (uwc-menu does not handle ArrowRight).
        const menu = curBtn?.closest('uwc-menu') as UwcMenu | null;
        if (menu) { e.preventDefault(); e.stopPropagation(); menu.show(); }
        break;
      }
      // Enter / Space fall through: native <button> activation fires `click`
      // (leaf selection) or uwc-menu's own keydown opens parent flyouts.
    }
  }

  private _onDrawerHide = (): void => {
    // Drawer dismissed via backdrop / Escape / close button — keep state in sync.
    if (this._drawerOpen) this._setDrawer(false);
  };

  /* ── Render ──────────────────────────────────────────────────────── */

  protected override render(): TemplateResult {
    return this._isMobile ? this._renderMobile() : this._renderDesktop();
  }

  /** Desktop: inline rail / accordion shell. */
  private _renderDesktop(): TemplateResult {
    return html`
      <aside
        part="base"
        class=${classMap({ 'sb': true, 'sb--collapsed': this.collapsed })}
        data-collapsed=${this.collapsed ? 'true' : 'false'}
      >
        <div class="sb__start" part="start"><slot name="start"></slot></div>

        <nav class="sb__nav" part="nav" aria-label=${this.ariaLabel} @keydown=${this._railKeydown}>
          ${this.collapsed ? this._renderCollapsed() : this._renderExpanded()}
        </nav>

        <div class="sb__end" part="end"><slot name="end"></slot></div>
      </aside>`;
  }

  /**
   * Mobile: hidden by default, opens as a `uwc-drawer` overlay (top layer,
   * backdrop, focus trap). The full accordion is shown — never the icon rail.
   * The same start/end slots flow into the drawer's header/footer.
   */
  private _renderMobile(): TemplateResult {
    const hasStart = this._hasSlotted('start');
    const hasEnd   = this._hasSlotted('end');

    return html`
      <uwc-drawer
        part="base"
        class="sb__drawer"
        position="left"
        ?open=${this._drawerOpen}
        size=${this.drawerSize}
        header=${hasStart ? '' : this.ariaLabel}
        closable
        close-on-outside-click
        aria-label=${this.ariaLabel}
        style="--uwc-drawer-bg: var(--_bg); --uwc-drawer-content-padding: 0; color: var(--_color);"
        @uwc-hide=${this._onDrawerHide}
      >
        ${hasStart
          ? html`<div slot="header" class="sb__start sb__start--drawer" part="start"><slot name="start"></slot></div>`
          : nothing}

        <nav class="sb__nav sb__nav--drawer" part="nav" aria-label=${this.ariaLabel}>
          ${this._renderExpanded()}
        </nav>

        ${hasEnd
          ? html`<div slot="footer" class="sb__end sb__end--drawer" part="end"><slot name="end"></slot></div>`
          : nothing}
      </uwc-drawer>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-sidebar': UwcSidebar; }
}
